import { NetworkStatus } from '@apollo/client'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BackgroundBox from '../../components/custom_components/BackgroundBox'
import { KudosCompanyCountryVisibilityFilterInput, KudosLegalEntityVisibilityFilterInput } from '../../types'
import { useKudosCompanyCountryVisibility, useKudosLegalEntityVisibility } from '../../graphql/kudos/useKudosQueries'
import PerSpacer from '../../components/custom_components/Spacer'
import { KUDOS_VISIBILITY_PAGE_SIZE } from '../../graphql/kudos/util'
import { NewLoadingAndErrors } from '../../components/custom_components/LoadingAndErrors/NewLoadingAndErrors'
import { KudosLegalEntityVisibilityTable } from './components/KudosLegalEntityVisibilityTable'
import KudosLegalEntityVisibilityInfoBox from './components/KudosLegalEntityVisibilityInfoBox'
import { KudosCompanyCountryVisibilityTable } from './components/KudosCompanyCountryVisibilityTable'
import { KudosCompanyCountryVisibilityInfoBox } from './components/KudosCompanyCountryVisibilityInfoBox'
import { ShowMoreButton } from '../reports/table/ShowMoreButton'

export const KudosVisibilityPage: FC<{ resetFiltersAfterVisibilityChange: () => void }> = ({ resetFiltersAfterVisibilityChange }) => {
  const { t } = useTranslation()
  const [firstLoadingDone, setFirstLoadingDone] = useState(false)
  const [visibilityPage, setVisibilityPage] = useState<'legal' | 'company'>('legal')

  const [legalFilters, setLegalFilters] = useState<KudosCompanyCountryVisibilityFilterInput>({
    status: { kudosDisabled: false, kudosEnabled: false },
    pagination: {
      offset: 0,
      limit: KUDOS_VISIBILITY_PAGE_SIZE,
    },
  })
  const [companyFilters, setCompanyFilters] = useState<KudosLegalEntityVisibilityFilterInput>({
    status: { kudosDisabled: false, kudosEnabled: false },
    pagination: {
      offset: 0,
      limit: KUDOS_VISIBILITY_PAGE_SIZE,
    },
  })

  const {
    data: legalData,
    loading: legalLoading,
    error: legalError,
    fetchMore: legalFetchMore,
    networkStatus: legalNetworkStatus,
  } = useKudosLegalEntityVisibility(legalFilters)
  const {
    data: companyData,
    loading: companyLoading,
    error: companyError,
    fetchMore: companyFetchMore,
    networkStatus: companyNetworkStatus,
  } = useKudosCompanyCountryVisibility(companyFilters)

  useEffect(() => {
    if (!!legalData?.getKudosLegalEntityVisibilityData && !!companyData?.getKudosCompanyCountryVisibilityData) {
      setFirstLoadingDone(true)
    }
  }, [legalData, companyData])

  const legalIsLoadingMore = legalNetworkStatus === NetworkStatus.fetchMore
  const legalShowMoreLayoutActive = !!legalData?.getKudosLegalEntityVisibilityData?.hasMore && (!legalLoading || legalIsLoadingMore)

  const companyIsLoadingMore = companyNetworkStatus === NetworkStatus.fetchMore
  const companyShowMoreLayoutActive =
    !!companyData?.getKudosCompanyCountryVisibilityData?.hasMore && (!companyLoading || companyIsLoadingMore)

  const handleLegalFiltersChange = (newFilters: KudosLegalEntityVisibilityFilterInput) => {
    setLegalFilters(newFilters)
  }
  const handleCompanyFiltersChange = (newFilters: KudosCompanyCountryVisibilityFilterInput) => {
    setCompanyFilters(newFilters)
  }

  const loadMoreLegals = () => {
    if (legalData && legalData.getKudosLegalEntityVisibilityData.hasMore) {
      legalFetchMore({
        variables: {
          filters: {
            ...legalFilters,
            pagination: {
              offset: legalData.getKudosLegalEntityVisibilityData.nextOffset || 0,
              limit: KUDOS_VISIBILITY_PAGE_SIZE,
            },
          },
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return previousResult
          }

          const previousLegal = previousResult.getKudosLegalEntityVisibilityData.kudosLegalEntityVisibilityList
          const fetchMoreLegal = fetchMoreResult.getKudosLegalEntityVisibilityData.kudosLegalEntityVisibilityList

          fetchMoreResult.getKudosLegalEntityVisibilityData.kudosLegalEntityVisibilityList = [...previousLegal, ...fetchMoreLegal]

          return { ...fetchMoreResult }
        },
      })
    }
  }
  const loadMoreCompanies = () => {
    if (companyData && companyData.getKudosCompanyCountryVisibilityData.hasMore) {
      companyFetchMore({
        variables: {
          filters: {
            ...companyFilters,
            pagination: {
              offset: companyData.getKudosCompanyCountryVisibilityData.nextOffset || 0,
              limit: KUDOS_VISIBILITY_PAGE_SIZE,
            },
          },
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return previousResult
          }

          const previousCompany = previousResult.getKudosCompanyCountryVisibilityData.kudosCompanyCountryVisibilityList
          const fetchMoreCompany = fetchMoreResult.getKudosCompanyCountryVisibilityData.kudosCompanyCountryVisibilityList

          fetchMoreResult.getKudosCompanyCountryVisibilityData.kudosCompanyCountryVisibilityList = [...previousCompany, ...fetchMoreCompany]

          return { ...fetchMoreResult }
        },
      })
    }
  }

  if ((legalError || legalLoading || companyError || companyLoading) && !firstLoadingDone) {
    return (
      <BackgroundBox r height="withTabs">
        <NewLoadingAndErrors
          isLoading={legalLoading || companyLoading}
          error={legalError || companyError}
          hasNoData={false}
          showSpinner
          t={t}
        />
      </BackgroundBox>
    )
  }

  const switchContentButtons = [
    { text: t('kudos.table.legalEntityName'), onClick: () => setVisibilityPage('legal') },
    { text: t('kudos.table.company'), onClick: () => setVisibilityPage('company') },
  ]

  return (
    <>
      {visibilityPage === 'legal' ? (
        <>
          <KudosLegalEntityVisibilityInfoBox
            filters={legalFilters}
            onFiltersChange={handleLegalFiltersChange}
            swichContentButtons={switchContentButtons}
          />
          <PerSpacer mb="xs" />
          <KudosLegalEntityVisibilityTable
            kudosLegalEntityVisibilityList={legalData?.getKudosLegalEntityVisibilityData?.kudosLegalEntityVisibilityList}
            loading={legalLoading}
            isLoadingMore={legalIsLoadingMore}
            error={legalError}
            filters={legalFilters}
            showMoreButtonVisible={legalShowMoreLayoutActive}
            resetFiltersAfterVisibilityChange={resetFiltersAfterVisibilityChange}
          />

          <ShowMoreButton visible={legalShowMoreLayoutActive} loadingMore={legalIsLoadingMore} onLoadMore={loadMoreLegals} />
        </>
      ) : (
        <>
          <KudosCompanyCountryVisibilityInfoBox
            filters={companyFilters}
            onFiltersChange={handleCompanyFiltersChange}
            swichContentButtons={switchContentButtons}
            resetFiltersAfterVisibilityChange={resetFiltersAfterVisibilityChange}
          />
          <PerSpacer mb="xs" />
          <KudosCompanyCountryVisibilityTable
            kudosCompanyCountryVisibilityList={companyData?.getKudosCompanyCountryVisibilityData?.kudosCompanyCountryVisibilityList}
            loading={companyLoading}
            isLoadingMore={companyIsLoadingMore}
            error={companyError}
            filters={companyFilters}
            showMoreButtonVisible={companyShowMoreLayoutActive}
            resetFiltersAfterVisibilityChange={resetFiltersAfterVisibilityChange}
          />

          <ShowMoreButton visible={companyShowMoreLayoutActive} loadingMore={companyIsLoadingMore} onLoadMore={loadMoreCompanies} />
        </>
      )}
    </>
  )
}
